import { useQuery } from '@tanstack/react-query'
import { Activity, Gift, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react'
import { useAppStore } from '../../store/useAppStore'
import { adminApi } from './adminApi'

interface LogEntry {
  id: string
  admin_user_id: string
  admin_line_uid: string
  admin_name: string | null
  action: string
  target_user_id: string | null
  target_line_uid: string | null
  details: Record<string, unknown> | null
  created_at: string
}

const ACTION_META: Record<string, { label: string; color: string; icon: typeof Activity }> = {
  manual_grant:  { label: 'มอบสิทธิ์',  color: '#b8945a', icon: Gift },
  approve_tx:    { label: 'อนุมัติ',    color: '#6b8a4f', icon: CheckCircle2 },
  reject_tx:     { label: 'ปฏิเสธ',     color: '#b85450', icon: XCircle },
  change_role:   { label: 'เปลี่ยนสิทธิ์', color: '#9a7a8e', icon: ShieldCheck },
}

function formatRelative(s: string): string {
  const ms = Date.now() - new Date(s).getTime()
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec} วินาทีที่แล้ว`
  if (sec < 3600) return `${Math.floor(sec / 60)} นาทีที่แล้ว`
  if (sec < 86400) return `${Math.floor(sec / 3600)} ชั่วโมงที่แล้ว`
  return `${Math.floor(sec / 86400)} วันที่แล้ว`
}

function describeLog(log: LogEntry): string {
  const meta = ACTION_META[log.action]
  const target = log.target_line_uid ? `${log.target_line_uid.slice(0, 12)}...` : 'system'
  switch (log.action) {
    case 'manual_grant': {
      const count = (log.details as { count?: number })?.count ?? 1
      return `มอบ ${count} ตั๋วให้ ${target}`
    }
    case 'approve_tx': {
      const ref = (log.details as { ref_code?: string })?.ref_code ?? '?'
      return `อนุมัติ ${ref} ของ ${target}`
    }
    case 'reject_tx': {
      const ref = (log.details as { ref_code?: string })?.ref_code ?? '?'
      return `ปฏิเสธ ${ref} ของ ${target}`
    }
    case 'change_role': {
      const d = log.details as { from?: string; to?: string }
      return `เปลี่ยนสิทธิ์ ${target} จาก ${d?.from} → ${d?.to}`
    }
    default:
      return `${meta?.label ?? log.action} ${target}`
  }
}

export default function AdminLogsPage() {
  const { lineUid } = useAppStore()

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'logs', lineUid],
    queryFn: () => adminApi.logs(lineUid!) as Promise<LogEntry[]>,
    enabled: !!lineUid,
    refetchInterval: 30_000,
  })

  return (
    <div className="p-4 md:p-6 lg:p-8">

      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ color: '#2d2820' }}>
          📋 บันทึกกิจกรรม
        </h1>
        <p className="text-sm" style={{ color: '#6b6354' }}>
          ติดตามการกระทำของผู้ดูแลระบบทั้งหมด
        </p>
      </div>

      <div className="rounded-2xl overflow-hidden"
           style={{ background: '#faf7ef', border: '1px solid rgba(0,0,0,0.04)' }}>
        {isLoading && (
          <div className="p-8 text-center text-sm" style={{ color: '#8a8270' }}>กำลังโหลด...</div>
        )}

        {data?.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: '#8a8270' }}>
            ยังไม่มีกิจกรรม
          </div>
        )}

        {data?.map(log => {
          const meta = ACTION_META[log.action] ?? { label: log.action, color: '#8a8270', icon: Activity }
          const Icon = meta.icon
          return (
            <div key={log.id}
                 className="flex items-start gap-3 p-3 md:p-4 border-b last:border-0"
                 style={{ borderColor: 'rgba(0,0,0,0.04)' }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ background: `${meta.color}22` }}>
                <Icon size={16} style={{ color: meta.color }} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: `${meta.color}22`, color: meta.color }}>
                    {meta.label}
                  </span>
                  <span className="text-[11px] font-semibold" style={{ color: '#2d2820' }}>
                    {log.admin_name ?? log.admin_line_uid.slice(0, 12)}
                  </span>
                </div>
                <div className="text-xs" style={{ color: '#6b6354' }}>
                  {describeLog(log)}
                </div>
                <div className="text-[10px] mt-0.5" style={{ color: '#a89e88' }}>
                  {formatRelative(log.created_at)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
